import React, { useCallback, useEffect, useState } from "react";
import api, { brl, dateBR } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Edit, Trash2, UserCircle, Eye, Search } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { CnpjLookupButton, formatCnpj } from "@/components/CnpjLookupButton";

const EMPTY = { name: "", email: "", phone: "", document: "", address: "", notes: "" };

export default function Customers() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [viewCust, setViewCust] = useState(null);
  const [viewSales, setViewSales] = useState([]);

  const load = useCallback(() => api.get("/customers").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ ...EMPTY, ...c }); setEditId(c.id); setOpen(true); };

  const save = async () => {
    if (!form.name) return toast.error("Nome é obrigatório");
    try {
      if (editId) await api.patch(`/customers/${editId}`, form);
      else await api.post("/customers", form);
      setOpen(false);
      toast.success(editId ? "Cliente atualizado" : "Cliente criado");
      // Defer refetch until Radix Dialog has fully unmounted (prevents removeChild race)
      setTimeout(load, 350);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const remove = async (id) => {
    if (!confirm("Excluir este cliente?")) return;
    try { await api.delete(`/customers/${id}`); toast.success("Excluído"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const openView = async (c) => {
    setViewCust(c);
    try {
      const { data } = await api.get(`/customers/${c.id}/sales`);
      setViewSales(data);
    } catch { setViewSales([]); }
  };

  const filtered = items.filter((c) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return c.name.toLowerCase().includes(s) || (c.email || "").toLowerCase().includes(s) || (c.phone || "").includes(q);
  });

  const avgTicket = (c) => (c.sales_count ? c.total_spent / c.sales_count : 0);

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">CRM</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Clientes</h1>
        </div>
        <Button onClick={openNew} className="bg-blue-600 hover:bg-blue-700" data-testid="new-customer-btn">
          <Plus className="w-4 h-4 mr-2" /> Novo Cliente
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="card-outline"><CardContent className="p-5">
          <div className="text-xs uppercase tracking-widest text-slate-500">Total de clientes</div>
          <div className="kpi-num text-3xl mt-2">{items.length}</div>
        </CardContent></Card>
        <Card className="card-outline"><CardContent className="p-5">
          <div className="text-xs uppercase tracking-widest text-slate-500">Faturado com clientes</div>
          <div className="kpi-num text-3xl mt-2 text-emerald-600">{brl(items.reduce((s, c) => s + (c.total_spent || 0), 0))}</div>
        </CardContent></Card>
        <Card className="card-outline"><CardContent className="p-5">
          <div className="text-xs uppercase tracking-widest text-slate-500">Clientes recorrentes</div>
          <div className="kpi-num text-3xl mt-2 text-blue-600">{items.filter((c) => (c.sales_count || 0) > 1).length}</div>
        </CardContent></Card>
      </div>

      <div className="relative max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input placeholder="Buscar por nome, e-mail ou telefone..." className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} data-testid="customer-search-input" />
      </div>

      <Card className="card-outline">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Nome</TableHead><TableHead>Contato</TableHead>
              <TableHead className="text-right">Compras</TableHead>
              <TableHead className="text-right">Total gasto</TableHead>
              <TableHead className="text-right">Ticket médio</TableHead>
              <TableHead>Última compra</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map((c) => (
                <TableRow key={c.id} data-testid={`customer-row-${c.id}`}>
                  <TableCell className="font-medium flex items-center gap-2">
                    <UserCircle className="w-4 h-4 text-slate-400" /> {c.name}
                    {(c.sales_count || 0) > 1 && <Badge className="bg-blue-600 text-[10px]">recorrente</Badge>}
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">
                    <div>{c.email || "—"}</div><div className="text-xs">{c.phone || ""}</div>
                  </TableCell>
                  <TableCell className="text-right kpi-num">{c.sales_count || 0}</TableCell>
                  <TableCell className="text-right kpi-num text-emerald-600">{brl(c.total_spent || 0)}</TableCell>
                  <TableCell className="text-right kpi-num">{brl(avgTicket(c))}</TableCell>
                  <TableCell className="text-sm">{c.last_purchase ? dateBR(c.last_purchase) : "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => openView(c)} data-testid={`view-cust-${c.id}`}><Eye className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-cust-${c.id}`}><Edit className="w-4 h-4" /></Button>
                    {user?.role === "admin" && (
                      <Button size="icon" variant="ghost" className="text-rose-600" onClick={() => remove(c.id)} data-testid={`del-cust-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">Nenhum cliente cadastrado</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="customer-dialog">
          <DialogHeader><DialogTitle className="font-display">{`${editId ? "Editar" : "Novo"} Cliente`}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Nome *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cust-name" /></div>
            <div><Label>E-mail</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="cust-email" /></div>
            <div><Label>Telefone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="cust-phone" /></div>
            <div><Label>CPF/CNPJ</Label>
              <div className="flex gap-1">
                <Input value={form.document} onChange={(e) => setForm({ ...form, document: e.target.value })} data-testid="cust-doc" placeholder="CNPJ: busca automática" />
                <CnpjLookupButton value={form.document} testId="cust-cnpj-lookup" onResult={(d) => setForm((f) => ({
                  ...f, name: d.trade_name || d.name || f.name, document: formatCnpj(d.cnpj),
                  email: d.email || f.email, phone: d.phone || f.phone, address: d.address || f.address,
                  notes: f.notes || (d.trade_name && d.name ? `Razão social: ${d.name}` : ""),
                }))} />
              </div></div>
            <div><Label>Endereço</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="cust-addr" /></div>
            <div className="col-span-2"><Label>Observações</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="cust-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-blue-600 hover:bg-blue-700" data-testid="save-customer-btn">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewCust} onOpenChange={(v) => !v && setViewCust(null)}>
        <DialogContent className="max-w-2xl" data-testid="customer-view-dialog">
          <DialogHeader><DialogTitle className="font-display">{viewCust?.name}</DialogTitle></DialogHeader>
          {viewCust && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div><div className="text-xs text-slate-500 uppercase">Compras</div><div className="kpi-num text-xl">{viewCust.sales_count || 0}</div></div>
                <div><div className="text-xs text-slate-500 uppercase">Total gasto</div><div className="kpi-num text-xl text-emerald-600">{brl(viewCust.total_spent || 0)}</div></div>
                <div><div className="text-xs text-slate-500 uppercase">Ticket médio</div><div className="kpi-num text-xl">{brl(avgTicket(viewCust))}</div></div>
              </div>
              <div className="text-sm space-y-1">
                {viewCust.email && <div><b>E-mail:</b> {viewCust.email}</div>}
                {viewCust.phone && <div><b>Telefone:</b> {viewCust.phone}</div>}
                {viewCust.document && <div><b>CPF/CNPJ:</b> {viewCust.document}</div>}
                {viewCust.address && <div><b>Endereço:</b> {viewCust.address}</div>}
                {viewCust.notes && <div><b>Notas:</b> {viewCust.notes}</div>}
              </div>
              <div>
                <h4 className="font-semibold mb-2 text-sm uppercase tracking-widest text-slate-500">Histórico de vendas</h4>
                {viewSales.length === 0 ? (
                  <div className="text-slate-400 text-sm">Sem compras ainda.</div>
                ) : (
                  <div className="max-h-64 overflow-y-auto space-y-1 text-sm">
                    {viewSales.map((s) => (
                      <div key={s.id} className="flex justify-between border-b border-slate-100 pb-1">
                        <span>{dateBR(s.created_at)}</span>
                        <span className="kpi-num">{brl(s.total)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
